import { Component } from '@theme/component';

/**
 * Keeps the complete server-rendered rich text available without JavaScript.
 * @extends {Component<{content: HTMLElement, toggle: HTMLButtonElement, label: HTMLElement}>}
 */
class KankacardCollectionDescription extends Component {
  requiredRefs = ['content', 'toggle', 'label'];
  #expanded = false;
  #frame = 0;
  #inlineSize = 0;
  #observer;
  #contentObserver;
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.addEventListener('focusin', this.#handleContentFocus, { signal: this.#controller.signal });
    this.#observer = new ResizeObserver(([entry]) => {
      if (!entry || Math.abs(entry.contentRect.width - this.#inlineSize) < .5) return;
      this.#inlineSize = entry.contentRect.width;
      this.#scheduleMeasurement();
    });
    this.#observer.observe(this);
    // A section morph can retain this element at the same width while replacing its content.
    this.#contentObserver = new MutationObserver(() => this.#scheduleMeasurement());
    this.#contentObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'ref', 'data-read-more', 'data-read-less'],
    });
    this.#scheduleMeasurement();
    document.fonts.ready.then(() => { if (this.isConnected) this.#scheduleMeasurement(); });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#expanded = false;
    this.#scheduleMeasurement();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#contentObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #scheduleMeasurement() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      if (!this.isConnected) return;
      super.updatedCallback();
      const { content, toggle } = this.refs;
      // Measure the same two-line layout at every width, then restore the user's state.
      this.setAttribute('data-collapsed', '');
      this.removeAttribute('data-expanded');
      const lineHeight = parseFloat(getComputedStyle(content).lineHeight);
      const needsToggle = content.scrollHeight > lineHeight * 2 + 1;
      if (toggle.hidden !== !needsToggle) toggle.hidden = !needsToggle;
      if (!needsToggle) this.#expanded = false;
      this.#renderState(needsToggle);
    });
  }

  #renderState(needsToggle = !this.refs.toggle.hidden) {
    this.toggleAttribute('data-collapsed', needsToggle && !this.#expanded);
    this.toggleAttribute('data-expanded', this.#expanded);
    this.refs.toggle.setAttribute('aria-expanded', String(this.#expanded));
    const label = this.#expanded ? this.refs.toggle.dataset.readLess : this.refs.toggle.dataset.readMore;
    if (this.refs.label.textContent !== label) this.refs.label.textContent = label;
  }

  /** Toggles with the native button's pointer, Enter or Space activation. */
  toggleDescription() {
    this.#expanded = !this.#expanded;
    this.#renderState();
  }

  /** Reveals rich-text links before they receive keyboard interaction. @param {FocusEvent} event */
  #handleContentFocus = (event) => {
    if (!this.#expanded && event.target instanceof Element && this.refs.content.contains(event.target)) {
      this.#expanded = true;
      this.#renderState();
    }
  };
}

if (!customElements.get('kankacard-collection-description')) {
  customElements.define('kankacard-collection-description', KankacardCollectionDescription);
}
